
/**
 * CN1 Shipping Protection Controller
 * Specializing in Sleek Theme & Shopify AJAX Cart API
 *
 * Provides:
 * 1. Instant UI updates for cart quantity, price, and toggle.
 * 2. Cart synchronization for shipping protection.
 * 3. Protection quantity equals total quantity of regular products.
 * 4. Smooth toggle enable/disable.
 * 5. Manual removal support.
 * 6. Shared toggle state between cart drawer and cart page.
 * 7. Automatic protection activation when a regular product is added.
 */
(() => {
  if (window.__cn1ShippingProtectionInitialized) return;
  window.__cn1ShippingProtectionInitialized = true;

  const CONFIG = {
    handle: 'cn1-shipping-protection',
    attributeKey: 'shipping_protection_toggle_state',
    titleKeywords: ['shipping protection', 'package protection'],
    productType: 'Shipping Protection',
  };

  class ShippingProtectionController {
    constructor() {
      this.isSyncing = false;
      this.pendingSync = null;
      this.lastProtectionQuantity = null;
      this.lastRegularQuantity = null;
      this.userToggledOff = false;
      this.autoEnablingProtection = false;
      this.manualRemovalInProgress = false;
      this.init();
    }

    init() {
      document.addEventListener('change', (e) => {
        if (e.target?.id === 'shippingProtectionCheckbox') {
          this.handleToggleChange(e.target.checked);
        }
      });

      document.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('a[is="cart-remove-item"]');
        if (!removeBtn) return;

        const lineItem = removeBtn.closest('.cart-item');
        if (!lineItem) return;

        const isProtection =
          lineItem.hasAttribute('data-cn1-protection') ||
          lineItem.classList.contains(CONFIG.handle) ||
          this.isProtectionItem({
            handle: lineItem.dataset.handle,
            title:
              lineItem.querySelector('.cart-item__title')?.textContent || '',
          });

        if (isProtection) this.handleManualRemoval();
      });

      document.addEventListener(
        'click',
        (e) => {
          if (
            e.target.closest(
              '.quantity__button, [name="plus"], [name="minus"], cart-quantity'
            )
          ) {
            requestAnimationFrame(() => {
              document.querySelectorAll('.btn--loading').forEach((el) => {
                el.classList.remove('btn--loading');
              });
            });

            setTimeout(() => {
              document.querySelectorAll('.btn--loading').forEach((el) => {
                el.classList.remove('btn--loading');
              });
            }, 60);
          }
        },
        true
      );

      // Listen for cart changes published by the Sleek theme.
      if (window.FoxTheme?.pubsub) {
        window.FoxTheme.pubsub.subscribe(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event?.cart && !event.cart.errors) {
              this.onCartUpdated(event.cart);
            }
          }
        );
      } else {
        document.addEventListener('cart:updated', (e) => {
          if (e.detail?.cart) this.onCartUpdated(e.detail.cart);
        });
      }

      // Detect add-to-cart requests made by fetch or XMLHttpRequest.
      this.installAddToCartListeners();

      this.checkInitialState();
    }

    installAddToCartListeners() {
      if (window.__cn1AddToCartListenersInstalled) return;
      window.__cn1AddToCartListenersInstalled = true;

      const controller = this;
      const isAddUrl = (url) =>
        /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url || '');

      // Catch AJAX requests using fetch().
      const originalFetch = window.fetch;

      window.fetch = async function (...args) {
        const input = args[0];
        const url =
          typeof input === 'string'
            ? input
            : input instanceof Request
              ? input.url
              : input?.url || '';

        const isCartAdd = isAddUrl(url);
        const response = await originalFetch.apply(this, args);

        if (isCartAdd && response.ok) {
          // Clone the response so the theme can still read its own response.
          try {
            const result = await response.clone().json();

            if (!controller.isProtectionItem(result)) {
              setTimeout(() => {
                controller.handleSuccessfulProductAdd();
              }, 150);
            }
          } catch (error) {
            // If the response cannot be parsed, inspect the latest cart.
            setTimeout(() => {
              controller.handleSuccessfulProductAdd();
            }, 150);
          }
        }

        return response;
      };

      // Catch AJAX requests using XMLHttpRequest.
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__cn1IsCartAdd = isAddUrl(String(url));
        return originalOpen.call(this, method, url, ...rest);
      };

      XMLHttpRequest.prototype.send = function (...args) {
        if (this.__cn1IsCartAdd) {
          this.addEventListener('load', () => {
            if (this.status >= 200 && this.status < 300) {
              let result = null;

              try {
                result = JSON.parse(this.responseText);
              } catch (_) {}

              if (result && controller.isProtectionItem(result)) return;

              setTimeout(() => {
                controller.handleSuccessfulProductAdd();
              }, 150);
            }
          });
        }

        return originalSend.apply(this, args);
      };
    }

    async handleSuccessfulProductAdd() {
     if (
  this.autoEnablingProtection ||
  this.manualRemovalInProgress
) return;

      try {
        const response = await fetch(this.getAjaxUrl('cart'), {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Could not retrieve the latest Shopify cart.');
        }

        const cart = await response.json();
        if (!cart || cart.errors) return;

        const { regularQuantity } = this.calculateQuantities(cart);
        if (regularQuantity <= 0) return;

        await this.enableProtectionAfterProductAdded(cart);
      } catch (error) {
        console.error(
          'CN1: Could not activate protection after adding a product:',
          error
        );
      }
    }

async enableProtectionAfterProductAdded(cart) {
  if (this.autoEnablingProtection || this.manualRemovalInProgress) return;

      this.autoEnablingProtection = true;
      this.userToggledOff = false;

      try {
        const config = window.FoxTheme?.utils?.fetchConfig
          ? window.FoxTheme.utils.fetchConfig()
          : {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
            };

        // Save the shared toggle state as ON.
        const stateResponse = await fetch(this.getAjaxUrl('update'), {
          ...config,
          body: JSON.stringify({
            attributes: { [CONFIG.attributeKey]: '1' },
          }),
        });

        const stateCart = await stateResponse.json();

        if (!stateResponse.ok || stateCart?.errors) {
          throw new Error('Could not enable the CN1 protection state.');
        }

        // Retrieve the latest cart before synchronizing protection quantity.
        const cartResponse = await fetch(this.getAjaxUrl('cart'), {
          headers: { Accept: 'application/json' },
        });

        const latestCart = await cartResponse.json();

        if (!cartResponse.ok || latestCart?.errors) {
          throw new Error('Could not retrieve the updated cart.');
        }

        latestCart.attributes = latestCart.attributes || {};
        latestCart.attributes[CONFIG.attributeKey] = '1';

        this.userToggledOff = false;

        const { regularQuantity, protectionQuantity } =
          this.calculateQuantities(latestCart);

        this.lastRegularQuantity = regularQuantity;
        this.lastProtectionQuantity = protectionQuantity;

        this.updateUI(regularQuantity, this.getUnitPrice());

        await this.syncProtection(latestCart);
      } catch (error) {
        console.error(
          'Could not automatically enable CN1 Shipping Protection:',
          error
        );
      } finally {
        this.autoEnablingProtection = false;
      }
    }

    getProtectionElement() {
      return document.querySelector('#CN1ShippingProtection');
    }

    getVariantId() {
      const el = this.getProtectionElement();
      return el?.dataset.variantId
        ? parseInt(el.dataset.variantId, 10)
        : null;
    }

    getUnitPrice() {
      const el = this.getProtectionElement();
      return el?.dataset.protectPrice
        ? parseInt(el.dataset.protectPrice, 10) || 0
        : 0;
    }

    getAjaxUrl(endpoint) {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const el = this.getProtectionElement();

      const routes = {
        update:
          el?.dataset?.updateUrl ||
          window.FoxTheme?.routes?.cart_update_url ||
          `${root}/cart/update`,
        add:
          el?.dataset?.addUrl ||
          window.FoxTheme?.routes?.cart_add_url ||
          `${root}/cart/add`,
        change:
          el?.dataset?.changeUrl ||
          window.FoxTheme?.routes?.cart_change_url ||
          `${root}/cart/change`,
        clear:
          window.FoxTheme?.routes?.cart_clear_url ||
          `${root}/cart/clear`,
        cart:
          el?.dataset?.cartUrl ||
          window.FoxTheme?.routes?.cart_url ||
          `${root}/cart`,
      };

      const url = routes[endpoint];
      if (!url) return `${root}/cart/${endpoint}.js`;

      return `${url.replace(/\.js$/, '').replace(/\/$/, '')}.js`;
    }

    formatMoney(cents) {
      if (
        window.FoxTheme?.Currency &&
        typeof window.FoxTheme.Currency.formatMoney === 'function'
      ) {
        const format =
          window.FoxTheme?.settings?.moneyFormat || '${{amount}}';
        return window.FoxTheme.Currency.formatMoney(cents, format);
      }

      const sym =
        document.querySelector('.currency-symbol')?.textContent?.trim() || '$';

      return `${sym}${(cents / 100).toFixed(2)}`;
    }

    isProtectionItem(item) {
      if (!item) return false;

      // Shopify /cart/add.js can return a line item or an items array.
      if (Array.isArray(item.items)) {
        return item.items.length > 0 &&
          item.items.every((entry) => this.isProtectionItem(entry));
      }

      const handle = (
        item.handle ||
        item.product_handle ||
        item.product?.handle ||
        ''
      ).toLowerCase();

      const title = (
        item.title ||
        item.product_title ||
        item.product?.title ||
        ''
      ).toLowerCase();

      const type = item.product_type || item.product?.type || '';

      return (
        handle === CONFIG.handle ||
        CONFIG.titleKeywords.some((keyword) => title.includes(keyword)) ||
        type === CONFIG.productType
      );
    }

    calculateQuantities(cart) {
      let regularQuantity = 0;
      let protectionQuantity = 0;
      const protectionItems = [];

      if (Array.isArray(cart?.items)) {
        cart.items.forEach((item) => {
          if (this.isProtectionItem(item)) {
            protectionQuantity += item.quantity;
            protectionItems.push(item);
          } else {
            regularQuantity += item.quantity;
          }
        });
      }

      return { regularQuantity, protectionQuantity, protectionItems };
    }

    isToggleEnabled(cart) {
      if (this.userToggledOff) return false;
      return cart?.attributes?.[CONFIG.attributeKey] === '1';
    }

    updateUI(targetQty, unitPrice) {
      unitPrice = unitPrice || this.getUnitPrice();

      const isOff = this.userToggledOff || targetQty <= 0;
      const effectiveQty = isOff ? 0 : targetQty;
      const totalPriceInCents =
        effectiveQty > 0 && unitPrice > 0 ? effectiveQty * unitPrice : 0;

      const formattedPlain = (totalPriceInCents / 100).toFixed(2);
      const formattedWithCurrency = this.formatMoney(totalPriceInCents);

      document.querySelectorAll('[data-cn1-protection-price]').forEach((el) => {
        el.textContent = formattedPlain;
      });

      document.querySelectorAll('#shippingProtectionCheckbox').forEach((cb) => {
        cb.checked = !isOff;
      });

      const protectionRows = document.querySelectorAll(
        '.cart-item[data-cn1-protection="true"], .cart-item.cn1-shipping-protection'
      );

      if (isOff) {
        protectionRows.forEach((row) => {
          row.style.display = 'none';
        });
      } else {
        protectionRows.forEach((row) => {
          row.style.display = '';
        });

        document.querySelectorAll('[data-cn1-item-qty]').forEach((el) => {
          el.innerHTML = `<span>Qty: ${effectiveQty}</span>`;
        });

        document.querySelectorAll('[data-cn1-item-price]').forEach((el) => {
          el.innerHTML = formattedWithCurrency;
        });
      }
    }

    onQuantityChanging(line, quantity) {
      if (this.userToggledOff) return;

      const isEnabled =
        document.querySelector('#shippingProtectionCheckbox')?.checked !== false;

      if (!isEnabled) return;

      let newRegularQty = 0;

      document.querySelectorAll('.cart-item').forEach((el) => {
        const isProt =
          el.hasAttribute('data-cn1-protection') ||
          el.classList.contains(CONFIG.handle);

        if (!isProt && el.style.display !== 'none') {
          const input = el.querySelector('input[name="updates[]"]');

          if (input) {
            newRegularQty +=
              input.dataset.index === String(line)
                ? parseInt(quantity, 10) || 0
                : parseInt(input.value, 10) || 0;
          }
        }
      });

      this.updateUI(newRegularQty);
    }

    getSyncPayload(line, quantity, target, key) {
      if (this.userToggledOff) return null;

      const protectionEl = document.querySelector(
        '.cart-item[data-cn1-protection="true"], .cart-item.cn1-shipping-protection'
      );

      if (!protectionEl) return null;

      const protectionKey =
        protectionEl.dataset.key ||
        protectionEl.querySelector('[data-key]')?.dataset?.key;

      if (!protectionKey) return null;

      const itemEl = target
        ? target.closest('.cart-item')
        : line
          ? document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`)
          : null;

      const lineKey =
        key ||
        target?.dataset?.key ||
        itemEl?.dataset?.key ||
        itemEl?.querySelector('[data-key]')?.dataset?.key;

      if (!lineKey) return null;

      const updateUrl = this.getAjaxUrl('update');

      if (lineKey === protectionKey || itemEl === protectionEl) {
        const newQty = parseInt(quantity, 10) || 0;

        return {
          url: updateUrl,
          body: {
            updates: { [protectionKey]: newQty },
            attributes:
              newQty === 0 ? { [CONFIG.attributeKey]: '0' } : {},
          },
          isEmptying: false,
        };
      }

      let newRegularQty = 0;

      document.querySelectorAll('.cart-item').forEach((el) => {
        const isProt =
          el.hasAttribute('data-cn1-protection') ||
          el.classList.contains(CONFIG.handle);

        if (!isProt && el.style.display !== 'none') {
          const elKey =
            el.dataset.key || el.querySelector('[data-key]')?.dataset?.key;
          const input = el.querySelector('input[name="updates[]"]');

          if (
            el === itemEl ||
            (lineKey && elKey === lineKey) ||
            (line && input?.dataset?.index === String(line))
          ) {
            newRegularQty += parseInt(quantity, 10) || 0;
          } else if (input) {
            newRegularQty += parseInt(input.value, 10) || 0;
          }
        }
      });

      const updates = {};
      const isEmptying = newRegularQty === 0;

      if (isEmptying) {
        document.querySelectorAll('.cart-item').forEach((el) => {
          const elKey =
            el.dataset.key || el.querySelector('[data-key]')?.dataset?.key;
          if (elKey) updates[elKey] = 0;
        });

        updates[lineKey] = 0;
        updates[protectionKey] = 0;
      } else {
        updates[lineKey] = parseInt(quantity, 10) || 0;
        updates[protectionKey] = newRegularQty;

        document
          .querySelectorAll(
            '.cart-item[data-cn1-protection="true"], .cart-item.cn1-shipping-protection'
          )
          .forEach((dupEl) => {
            const dupKey =
              dupEl.dataset.key ||
              dupEl.querySelector('[data-key]')?.dataset?.key;

            if (dupKey && dupKey !== lineKey && dupKey !== protectionKey) {
              updates[dupKey] = 0;
            }
          });
      }

      return {
        url: updateUrl,
        body: {
          updates,
          attributes: isEmptying
            ? { [CONFIG.attributeKey]: '0' }
            : undefined,
        },
        isEmptying,
      };
    }

    async handleToggleChange(checked) {
      const unitPrice = this.getUnitPrice();
      const variantId = this.getVariantId();

      if (!checked) {
        this.userToggledOff = true;
        this.updateUI(0, unitPrice);

        try {
          const sections = this.getSectionsToBundle();
          const fetchConfig = window.FoxTheme?.utils?.fetchConfig
            ? window.FoxTheme.utils.fetchConfig()
            : {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
              };

          const protectionEls = document.querySelectorAll(
            '.cart-item[data-cn1-protection="true"], .cart-item.cn1-shipping-protection'
          );

          const updates = {};
          protectionEls.forEach((el) => {
            const key =
              el.dataset.key || el.querySelector('[data-key]')?.dataset?.key;
            if (key) updates[key] = 0;
          });

          if (Object.keys(updates).length > 0) {
            const res = await fetch(this.getAjaxUrl('update'), {
              ...fetchConfig,
              body: JSON.stringify({
                updates,
                attributes: { [CONFIG.attributeKey]: '0' },
                sections,
              }),
            });

            const updatedCart = await res.json();

            if (updatedCart && !updatedCart.errors && window.FoxTheme?.pubsub) {
              window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                { cart: updatedCart }
              );
            }
          } else {
            await this.fetchCurrentCartAndSync(false);
          }
        } catch (e) {
          console.error('Error toggling off protection:', e);
          await this.fetchCurrentCartAndSync(false);
        }

        return;
      }

      this.userToggledOff = false;

      let totalQty = 0;
      document.querySelectorAll('.cart-item').forEach((itemEl) => {
        const isProt =
          itemEl.hasAttribute('data-cn1-protection') ||
          itemEl.classList.contains(CONFIG.handle);

        if (!isProt && itemEl.style.display !== 'none') {
          const input = itemEl.querySelector('input[name="updates[]"]');
          if (input) totalQty += parseInt(input.value, 10) || 0;
        }
      });

      if (totalQty === 0) {
        this.updateUI(0, unitPrice);
        return;
      }

      this.updateUI(totalQty, unitPrice);

      try {
        const sections = this.getSectionsToBundle();
        const fetchConfig = window.FoxTheme?.utils?.fetchConfig
          ? window.FoxTheme.utils.fetchConfig()
          : {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
            };

        const protectionEl = document.querySelector(
          '.cart-item[data-cn1-protection="true"], .cart-item.cn1-shipping-protection'
        );

        const protectionKey =
          protectionEl?.dataset?.key ||
          protectionEl?.querySelector('[data-key]')?.dataset?.key;

        if (protectionKey) {
          const res = await fetch(this.getAjaxUrl('update'), {
            ...fetchConfig,
            body: JSON.stringify({
              updates: { [protectionKey]: totalQty },
              attributes: { [CONFIG.attributeKey]: '1' },
              sections,
            }),
          });

          const updatedCart = await res.json();

          if (updatedCart && !updatedCart.errors && window.FoxTheme?.pubsub) {
            window.FoxTheme.pubsub.publish(
              window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              { cart: updatedCart }
            );
          }
        } else if (variantId) {
          const addRes = await fetch(this.getAjaxUrl('add'), {
            ...fetchConfig,
            body: JSON.stringify({
              items: [{ id: variantId, quantity: totalQty }],
            }),
          });

          const addCart = await addRes.json();

          if (!addRes.ok || addCart?.errors) {
            throw new Error('Could not add CN1 Shipping Protection.');
          }

          const res = await fetch(this.getAjaxUrl('update'), {
            ...fetchConfig,
            body: JSON.stringify({
              attributes: { [CONFIG.attributeKey]: '1' },
              sections,
            }),
          });

          const updatedCart = await res.json();

          if (updatedCart && !updatedCart.errors && window.FoxTheme?.pubsub) {
            window.FoxTheme.pubsub.publish(
              window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              { cart: updatedCart }
            );
          }
        } else {
          await this.fetchCurrentCartAndSync(true);
        }
      } catch (e) {
        console.error('Error toggling on protection:', e);
        await this.fetchCurrentCartAndSync(true);
      }
    }




async handleManualRemoval() {
  if (this.manualRemovalInProgress) return;

  this.manualRemovalInProgress = true;
  this.userToggledOff = true;

  // Do not let an older cart event trigger another sync.
  this.pendingSync = null;
  this.updateUI(0, this.getUnitPrice());

  try {
    const fetchConfig = window.FoxTheme?.utils?.fetchConfig
      ? window.FoxTheme.utils.fetchConfig()
      : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };

    const cartResponse = await fetch(this.getAjaxUrl('cart'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!cartResponse.ok) {
      throw new Error('Could not retrieve the cart.');
    }

    const cart = await cartResponse.json();
    const { protectionItems } = this.calculateQuantities(cart);

    const updates = {};

    protectionItems.forEach((item) => {
      if (item.key) updates[item.key] = 0;
    });

    const response = await fetch(this.getAjaxUrl('update'), {
      ...fetchConfig,
      body: JSON.stringify({
        updates,
        attributes: {
          [CONFIG.attributeKey]: '0',
        },
      }),
    });

    const updatedCart = await response.json();

    if (!response.ok || updatedCart?.errors) {
      throw new Error('Could not remove shipping protection.');
    }

    this.pendingSync = null;
    this.userToggledOff = true;
    this.lastProtectionQuantity = 0;

    // Verify the actual Shopify cart after removal.
    const verifyResponse = await fetch(this.getAjaxUrl('cart'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!verifyResponse.ok) {
      throw new Error('Could not verify the updated cart.');
    }

    const verifiedCart = await verifyResponse.json();
    const { protectionItems: remainingItems } =
      this.calculateQuantities(verifiedCart);

    if (remainingItems.length > 0) {
      console.error(
        'CN1: Protection is still present in the Shopify cart.',
        remainingItems
      );
      return;
    }

    this.lastRegularQuantity =
      this.calculateQuantities(verifiedCart).regularQuantity;

    this.updateUI(0, this.getUnitPrice());

    if (window.FoxTheme?.pubsub) {
      window.FoxTheme.pubsub.publish(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        { cart: updatedCart }
      );
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: verifiedCart },
      })
    );
  } catch (error) {
    console.error('Error removing CN1 Shipping Protection:', error);
  } finally {
    this.pendingSync = null;
    this.manualRemovalInProgress = false;
  }
}





async fetchCurrentCartAndSync(desiredState) {
  try {
    const fetchConfig = window.FoxTheme?.utils?.fetchConfig
      ? window.FoxTheme.utils.fetchConfig()
      : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };

    // Persist an explicitly requested toggle state in Shopify.
    if (desiredState === true || desiredState === false) {
      const stateResponse = await fetch(this.getAjaxUrl('update'), {
        ...fetchConfig,
        body: JSON.stringify({
          attributes: {
            [CONFIG.attributeKey]: desiredState ? '1' : '0',
          },
        }),
      });

      const stateCart = await stateResponse.json();

      if (!stateResponse.ok || stateCart?.errors) {
        throw new Error('Could not save protection toggle state.');
      }

      this.userToggledOff = !desiredState;
    }

    if (this.manualRemovalInProgress) {
      this.userToggledOff = true;
      return;
    }

    const res = await fetch(this.getAjaxUrl('cart'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!res.ok) {
      throw new Error('Could not retrieve the latest cart.');
    }

    const cart = await res.json();
    if (!cart || cart.errors) return;

    cart.attributes = cart.attributes || {};

    if (
      !Object.prototype.hasOwnProperty.call(
        cart.attributes,
        CONFIG.attributeKey
      )
    ) {
      cart.attributes[CONFIG.attributeKey] = '0';

      const saveResponse = await fetch(this.getAjaxUrl('update'), {
        ...fetchConfig,
        body: JSON.stringify({
          attributes: { [CONFIG.attributeKey]: '0' },
        }),
      });

      const savedCart = await saveResponse.json();

      if (!saveResponse.ok || savedCart?.errors) {
        throw new Error('Could not initialize protection state.');
      }

      cart.attributes[CONFIG.attributeKey] = '0';
    }

    if (this.manualRemovalInProgress) {
      this.userToggledOff = true;
      return;
    }

    this.userToggledOff =
      cart.attributes[CONFIG.attributeKey] !== '1';

    const { regularQuantity, protectionQuantity } =
      this.calculateQuantities(cart);

    this.lastRegularQuantity = regularQuantity;
    this.lastProtectionQuantity = protectionQuantity;

    await this.syncProtection(cart);
  } catch (error) {
    console.error('Error fetching cart for sync:', error);
  }
}



    checkInitialState() {
      this.userToggledOff = true;
      this.updateUI(0, this.getUnitPrice());

      const loadCart = () => this.fetchCurrentCartAndSync(null);

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', loadCart, { once: true });
      } else {
        loadCart();
      }
    }

    onCartUpdated(cart) {
      const { regularQuantity, protectionQuantity } =
        this.calculateQuantities(cart);

      this.lastRegularQuantity = regularQuantity;

      if (
        regularQuantity > 0 &&
        protectionQuantity === 0 &&
        this.lastProtectionQuantity > 0 &&
        !this.userToggledOff
      ) {
        this.userToggledOff = true;
        this.updateUI(0, this.getUnitPrice());
      }

      this.lastProtectionQuantity = protectionQuantity;
      this.syncProtection(cart);
    }

    getSectionsToBundle() {
      const sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections },
        })
      );

      return Array.from(new Set(sections));
    }

async syncProtection(cart) {
  if (!cart || this.manualRemovalInProgress) return;

      if (this.isSyncing) {
        this.pendingSync = cart;
        return;
      }

      if (window.FoxTheme?.isCartOperationInProgress) {
        setTimeout(() => this.syncProtection(cart), 80);
        return;
      }

      const {
        regularQuantity,
        protectionQuantity,
        protectionItems,
      } = this.calculateQuantities(cart);

      const isEnabled = this.isToggleEnabled(cart);
      const targetProtectionQty =
        isEnabled && regularQuantity > 0 ? regularQuantity : 0;

      this.updateUI(targetProtectionQty, this.getUnitPrice());

      const hasDuplicateLines = protectionItems.length > 1;
      const needsSync =
        protectionQuantity !== targetProtectionQty || hasDuplicateLines;

      if (!needsSync) return;

      this.isSyncing = true;

      try {
        const sections = this.getSectionsToBundle();
        const updateUrl = this.getAjaxUrl('update');
        const addUrl = this.getAjaxUrl('add');

        const fetchConfig = window.FoxTheme?.utils?.fetchConfig
          ? window.FoxTheme.utils.fetchConfig()
          : {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
            };

        if (targetProtectionQty === 0) {
          if (protectionItems.length > 0) {
            const updates = {};

            protectionItems.forEach((item) => {
              updates[item.key || item.id] = 0;
            });

            const res = await fetch(updateUrl, {
              ...fetchConfig,
              body: JSON.stringify({
                updates,
                attributes: { [CONFIG.attributeKey]: '0' },
                sections,
              }),
            });

            const updatedCart = await res.json();

            if (updatedCart && !updatedCart.errors && window.FoxTheme?.pubsub) {
              window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                { cart: updatedCart }
              );
            }
          }
        } else {
          const variantId = this.getVariantId();

          if (!variantId) {
            console.warn('CN1 Shipping Protection variant ID not found');
            return;
          }

          if (protectionItems.length === 0) {
            const addRes = await fetch(addUrl, {
              ...fetchConfig,
              body: JSON.stringify({
                items: [{ id: variantId, quantity: targetProtectionQty }],
              }),
            });

            const addCart = await addRes.json();

            if (!addRes.ok || addCart?.errors) {
              console.error(
                'Error adding shipping protection:',
                addCart?.errors || addRes.statusText
              );
              return;
            }

            const res = await fetch(updateUrl, {
              ...fetchConfig,
              body: JSON.stringify({
                attributes: { [CONFIG.attributeKey]: '1' },
                sections,
              }),
            });

            const updatedCart = await res.json();

            if (updatedCart && !updatedCart.errors && window.FoxTheme?.pubsub) {
              window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                { cart: updatedCart }
              );
            }
          } else {
            const updates = {};

            protectionItems.forEach((item, idx) => {
              updates[item.key || item.id] =
                idx === 0 ? targetProtectionQty : 0;
            });

            const res = await fetch(updateUrl, {
              ...fetchConfig,
              body: JSON.stringify({
                updates,
                attributes: { [CONFIG.attributeKey]: '1' },
                sections,
              }),
            });

            const updatedCart = await res.json();

            if (updatedCart && !updatedCart.errors && window.FoxTheme?.pubsub) {
              window.FoxTheme.pubsub.publish(
                window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
                { cart: updatedCart }
              );
            }
          }
        }
      } catch (err) {
        console.error('Error synchronizing shipping protection:', err);
      } finally {
        this.isSyncing = false;

        if (this.pendingSync) {
          const nextCart = this.pendingSync;
          this.pendingSync = null;
          this.syncProtection(nextCart);
        }
      }
    }
  }

  if (!window.Cn1ShippingProtection) {
    window.Cn1ShippingProtection = new ShippingProtectionController();
  }
})();
